import { Controller } from "@hotwired/stimulus"

// The open order bar closes the ways a sheet is expected to: Esc, or a tap anywhere
// else on the page. It used to close only with its own arrow, so a customer who opened
// it to look had to find that arrow again to get the page back (B107).
//
// Either way focus goes back to the bar's toggle, so a keyboard user carries on from
// where the bar was rather than from the top of the document.
//
// A tap outside closes the bar and does nothing else. On a phone the bar covers half
// the screen, and the tap that dismisses it lands on whatever happened to be behind —
// a Reserve button, a link. So the first outside pointerdown is taken: it is cancelled,
// and so is the click that follows it.
export default class extends Controller {
  static targets = ["details", "summary", "hint"]

  connect() {
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.swallowClick = this.swallowClick.bind(this)
    this.listen()
  }

  disconnect() {
    this.stopListening()
    this.stopSwallowing()
  }

  // Bound to the <details> toggle event, which fires for the arrow and for us alike.
  toggled() {
    if (this.detailsTarget.open) this.dismissHint()
    this.listen()
  }

  // The "click Continue booking" bubble shown after a room is added (B133). Opening the
  // bar answers it as well as its own button does.
  dismissHint() {
    if (this.hasHintTarget) this.hintTarget.remove()
  }

  listen() {
    this.stopListening()
    if (!this.detailsTarget.open) return

    // Capture, so the bar sees the press before anything on the page reacts to it.
    document.addEventListener("pointerdown", this.onPointerDown, true)
    document.addEventListener("keydown", this.onKeyDown)
  }

  stopListening() {
    document.removeEventListener("pointerdown", this.onPointerDown, true)
    document.removeEventListener("keydown", this.onKeyDown)
  }

  onKeyDown(event) {
    if (event.key !== "Escape" || this.confirmOpen) return

    event.preventDefault()
    this.close()
  }

  onPointerDown(event) {
    if (this.element.contains(event.target) || this.confirmOpen) return

    event.preventDefault()
    event.stopPropagation()
    this.startSwallowing()
    this.close()
  }

  close() {
    this.detailsTarget.open = false
    this.stopListening()
    this.summaryTarget.focus({ preventScroll: true })
  }

  // The click after a cancelled pointerdown still fires, so it is caught on the way
  // down and dropped. If no click comes — the finger moved and it became a scroll —
  // the trap is lifted shortly after rather than eating the next real tap.
  startSwallowing() {
    this.stopSwallowing()
    document.addEventListener("click", this.swallowClick, true)
    this.swallowTimer = setTimeout(() => this.stopSwallowing(), 600)
  }

  stopSwallowing() {
    document.removeEventListener("click", this.swallowClick, true)
    clearTimeout(this.swallowTimer)
  }

  swallowClick(event) {
    event.preventDefault()
    event.stopPropagation()
    this.stopSwallowing()
  }

  // Discard asks first, in a dialog outside the bar. Pressing in that dialog is not
  // pressing outside the bar, and its own Esc belongs to it.
  get confirmOpen() {
    const modal = document.getElementById("confirm-modal")
    return modal && !modal.classList.contains("hidden")
  }
}
